import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  FormControlLabel,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Rating,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import CloseIcon from '@mui/icons-material/Close';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB, pl as plPL } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { adminApiClient } from '../../adminApiClient';
import { Space } from '../../models/Space';
import { SpaceRankBoost, SpaceRankBoostType } from '../../models/SpaceRankBoost';
import { FakeUserInfo, AdminCreateSpaceReviewRequest } from '../../models/FakeUser';
import { format, addDays } from 'date-fns';
import { useThemeContext } from '../../contexts/ThemeContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`location-tabpanel-${index}`}
      aria-labelledby={`location-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const SpacesPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mode } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(0);
  const [boostLoading, setBoostLoading] = useState<string | null>(null);
  const [boostDialogOpen, setBoostDialogOpen] = useState(false);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [boostPoints, setBoostPoints] = useState<number>(100);
  const [expirationDate, setExpirationDate] = useState<Date | null>(addDays(new Date(), 1));
  const [boostVisible, setBoostVisible] = useState<boolean>(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewSpaceId, setReviewSpaceId] = useState<string | null>(null);
  const [fakeUsers, setFakeUsers] = useState<FakeUserInfo[]>([]);
  const [selectedFakeUserId, setSelectedFakeUserId] = useState<string>('');
  const [reviewRating, setReviewRating] = useState<number>(5);
  const [reviewComment, setReviewComment] = useState<string>('');
  const [reviewLoading, setReviewLoading] = useState(false);

  useEffect(() => {
    const fetchSpaces = async () => {
      try {
        setLoading(true);
        const data = await adminApiClient.getAllSpaces();
        setSpaces(data);
      } catch (err) {
        setError(t('spaces.fetchError', 'Failed to fetch spaces'));
        console.error('Error fetching spaces:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSpaces();
  }, [t]);

  // Group spaces by location and sort by score (highest first)
  const spacesByLocation = useMemo(() => {
    const grouped: Record<string, Space[]> = {};
    spaces.forEach(space => {
      // Use location field, fall back to city if not available
      const location = space.location?.toString() || space.city || 'Unknown';
      if (!grouped[location]) {
        grouped[location] = [];
      }
      grouped[location].push(space);
    });
    // Sort each location's spaces by score (highest first)
    Object.keys(grouped).forEach(location => {
      grouped[location].sort((a, b) => b.rank - a.rank);
    });
    return grouped;
  }, [spaces]);

  // Get sorted list of locations by space count (descending)
  const locations = useMemo(() => {
    return Object.keys(spacesByLocation).sort((a, b) => {
      return spacesByLocation[b].length - spacesByLocation[a].length;
    });
  }, [spacesByLocation]);

  const handleLocationTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedLocationIndex(newValue);
  };

  const getCurrentLocale = () => {
    const language = localStorage.getItem('language') || 'en';
    return language === 'pl' ? plPL : enGB;
  };

  const handleBoost = (spaceId: string) => {
    setSelectedSpaceId(spaceId);
    setBoostPoints(100);
    setExpirationDate(addDays(new Date(), 1));
    setBoostVisible(true);
    setBoostDialogOpen(true);
  };

  const handleCloseBoostDialog = () => {
    setBoostDialogOpen(false);
    setSelectedSpaceId(null);
    setBoostPoints(100);
    setExpirationDate(addDays(new Date(), 1));
    setBoostVisible(true);
  };

  const handleSubmitBoost = async () => {
    if (!selectedSpaceId || !expirationDate) {
      setError(t('spaces.boostValidationError', 'Please select points and expiration date'));
      return;
    }

    try {
      setBoostLoading(selectedSpaceId);
      const boost: SpaceRankBoost = {
        type: SpaceRankBoostType.ADMIN_BOOST,
        points: boostPoints,
        expiresAt: format(expirationDate, 'yyyy-MM-dd'),
        visible: boostVisible,
      };
      await adminApiClient.addSpaceRankBoost(selectedSpaceId, boost);
      handleCloseBoostDialog();
      // Refresh spaces to get updated ranks
      const data = await adminApiClient.getAllSpaces();
      setSpaces(data);
    } catch (err) {
      setError(t('spaces.boostError', 'Failed to boost space'));
      console.error('Error boosting space:', err);
    } finally {
      setBoostLoading(null);
    }
  };

  const handleDeleteBoost = async (spaceId: string, boostId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent tooltip from opening
    try {
      setBoostLoading(spaceId);
      await adminApiClient.removeSpaceRankBoost(spaceId, { boostId });
      // Refresh spaces to get updated ranks
      const data = await adminApiClient.getAllSpaces();
      setSpaces(data);
    } catch (err) {
      setError(t('spaces.deleteBoostError', 'Failed to delete boost'));
      console.error('Error deleting boost:', err);
    } finally {
      setBoostLoading(null);
    }
  };

  const handleOpenReviewDialog = async (spaceId: string) => {
    setReviewSpaceId(spaceId);
    setSelectedFakeUserId('');
    setReviewRating(5);
    setReviewComment('');
    setReviewDialogOpen(true);
    
    // Fetch fake users when opening the dialog
    try {
      const data = await adminApiClient.listFakeUsers();
      setFakeUsers(data);
    } catch (err) {
      setError(t('spaces.fetchFakeUsersError', 'Failed to fetch fake users'));
      console.error('Error fetching fake users:', err);
    }
  };

  const handleCloseReviewDialog = () => {
    setReviewDialogOpen(false);
    setReviewSpaceId(null);
    setSelectedFakeUserId('');
    setReviewRating(5);
    setReviewComment('');
  };

  const handleSubmitReview = async () => {
    if (!reviewSpaceId || !selectedFakeUserId || !reviewComment.trim()) {
      setError(t('spaces.reviewValidationError', 'Please select a user and provide a comment'));
      return;
    }

    if (reviewRating < 1 || reviewRating > 5) {
      setError(t('spaces.reviewRatingError', 'Please provide a rating between 1 and 5'));
      return;
    }

    try {
      setReviewLoading(true);
      const request: AdminCreateSpaceReviewRequest = {
        userId: selectedFakeUserId,
        rating: reviewRating,
        comment: reviewComment.trim(),
      };
      await adminApiClient.createReviewOnBehalf(reviewSpaceId, request);
      handleCloseReviewDialog();
      // Optionally refresh spaces to show updated review stats
      const data = await adminApiClient.getAllSpaces();
      setSpaces(data);
    } catch (err) {
      setError(t('spaces.createReviewError', 'Failed to create review'));
      console.error('Error creating review:', err);
    } finally {
      setReviewLoading(false);
    }
  };

  const boostPointOptions = [100, 200, 300, 400, 500, 600, 700, 800, 900, 1000];

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('spaces.name', 'Name'),
      flex: 2,
    },
    {
      field: 'addressLine1',
      headerName: t('spaces.address', 'Address'),
      flex: 3,
      renderCell: (params: any) => (
        <Box>
          <Typography variant="body2">
            {params.row.addressLine1}
          </Typography>
          {params.row.addressLine2 && (
            <Typography variant="body2" color="text.secondary">
              {params.row.addressLine2}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: t('spaces.actions', 'Actions'),
      width: 150,
      sortable: false,
      renderCell: (params: any) => {
        const space = params.row as Space;
        return (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<RateReviewIcon />}
            onClick={() => handleOpenReviewDialog(space.id)}
          >
            {t('spaces.review', 'Review')}
          </Button>
        );
      },
    },
    {
      field: 'rank',
      headerName: t('spaces.score', 'Score'),
      width: 400,
      sortable: false,
      renderCell: (params: any) => {
        const space = params.row as Space;
        const isLoading = boostLoading === space.id;
        // Check both space.boosts and rankCalculationDetails.boosts
        const boosts = space.boosts || space.rankCalculationDetails?.boosts || [];
        const now = new Date();
        const activeBoosts = boosts.filter(boost => {
          const expiresAt = new Date(boost.expiresAt);
          return expiresAt >= now;
        });

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, width: '100%' }}>
            <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 60 }}>
              {t('spaces.score', 'Score')}: {space.rank}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={isLoading ? <CircularProgress size={16} /> : <BoltIcon />}
              onClick={() => handleBoost(space.id)}
              disabled={isLoading}
              sx={{ minWidth: 70 }}
            >
              {isLoading ? null : t('spaces.boost', 'Boost')}
            </Button>
            {activeBoosts.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                {activeBoosts.map((boost, index) => (
                  <Tooltip 
                    key={boost.id || `boost-${index}`}
                    title={`Expires: ${format(new Date(boost.expiresAt), 'MMM d, yyyy')}`}
                    arrow
                  >
                    <Chip
                      icon={<BoltIcon sx={{ fontSize: '0.9rem !important' }} />}
                      label={`+${boost.points} ${boost.type}`}
                      onDelete={boost.id ? (e) => handleDeleteBoost(space.id, boost.id!, e) : undefined}
                      deleteIcon={<CloseIcon sx={{ fontSize: '0.9rem !important' }} />}
                      size="small"
                      color="warning"
                      variant="filled"
                      sx={{ 
                        fontSize: '0.75rem', 
                        height: 24,
                        fontWeight: 600,
                        '& .MuiChip-icon': {
                          color: 'inherit',
                        },
                        '& .MuiChip-deleteIcon': {
                          color: 'inherit',
                          fontSize: '0.9rem',
                          '&:hover': {
                            color: 'error.main',
                          }
                        },
                        '&:hover': {
                          opacity: 0.9,
                        }
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            )}
          </Box>
        );
      },
    },
  ];

  if (loading && spaces.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1600, width: '100%', mx: 'auto' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h4" component="h1">
            {t('spaces.title', 'Spaces')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {spaces.length} {spaces.length === 1 ? 'space' : 'spaces'}
          </Typography>
        </Box>
        <Divider sx={{ mb: { xs: 2, md: 3 } }} />

        {locations.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              {t('spaces.noSpaces', 'No spaces found')}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Location Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={selectedLocationIndex}
                onChange={handleLocationTabChange}
                aria-label="location tabs"
                variant={isMobile ? 'scrollable' : 'standard'}
                scrollButtons="auto"
                sx={{
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                    backgroundColor: theme.palette.primary.main,
                  },
                }}
              >
                {locations.map((location, index) => (
                  <Tab
                    key={location}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {location}
                        <Chip
                          label={spacesByLocation[location].length}
                          size="small"
                          sx={{ height: 20, minWidth: 20, fontSize: '0.75rem' }}
                        />
                      </Box>
                    }
                    value={index}
                    sx={{
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      minHeight: 56,
                      px: 3,
                    }}
                  />
                ))}
              </Tabs>
            </Box>

            {/* Spaces Table for Current Location */}
            {locations.map((location, index) => (
              <TabPanel key={location} value={selectedLocationIndex} index={index}>
                {isMobile ? (
                  <Box sx={{ mt: 2 }}>
                    {spacesByLocation[location].length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="body1" color="text.secondary">
                          {t('spaces.noSpacesInLocation', 'No spaces found in this location')}
                        </Typography>
                      </Box>
                    ) : (
                      <Stack spacing={2}>
                        {spacesByLocation[location].map((space) => {
                          const isLoading = boostLoading === space.id;
                          return (
                            <Card key={space.id} variant="outlined" sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
                                  #{space.rank}
                                </Typography>
                                <Typography variant="h6">
                                  {space.name}
                                </Typography>
                              </Box>
                              <Stack spacing={1} sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {space.addressLine1}
                                  {space.addressLine2 && `, ${space.addressLine2}`}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {space.city}
                                </Typography>
                                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" fontWeight="medium">
                                    {t('spaces.score', 'Score')}: {space.rank}
                                  </Typography>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <BoltIcon />}
                                    onClick={() => handleBoost(space.id)}
                                    disabled={isLoading}
                                  >
                                    {isLoading ? null : t('spaces.boost', 'Boost')}
                                  </Button>
                                </Box>
                                {(() => {
                                  const boosts = space.boosts || space.rankCalculationDetails?.boosts || [];
                                  const activeBoosts = boosts.filter(boost => new Date(boost.expiresAt) >= new Date());
                                  return activeBoosts.length > 0 && (
                                    <Box sx={{ 
                                      mt: 1.5, 
                                      pt: 1.5, 
                                      borderTop: '1px solid', 
                                      borderColor: 'divider',
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      gap: 0.75 
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                        <BoltIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                                        <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                          {t('spaces.activeBoosts', 'Active Boosts')}
                                        </Typography>
                                      </Box>
                                      {activeBoosts.map((boost, index) => (
                                        <Tooltip 
                                          key={boost.id || `boost-${index}`}
                                          title={`Expires: ${format(new Date(boost.expiresAt), 'MMM d, yyyy')}`}
                                          arrow
                                        >
                                          <Chip
                                            icon={<BoltIcon sx={{ fontSize: '1rem !important' }} />}
                                            label={`+${boost.points} ${boost.type}`}
                                            onDelete={boost.id ? (e) => handleDeleteBoost(space.id, boost.id!, e) : undefined}
                                            deleteIcon={<CloseIcon sx={{ fontSize: '1rem !important' }} />}
                                            size="small"
                                            color="warning"
                                            variant="filled"
                                            sx={{ 
                                              fontSize: '0.8rem', 
                                              height: 26,
                                              fontWeight: 600,
                                              width: 'fit-content',
                                              '& .MuiChip-icon': {
                                                color: 'inherit',
                                              },
                                              '& .MuiChip-deleteIcon': {
                                                color: 'inherit',
                                                fontSize: '1rem',
                                                '&:hover': {
                                                  color: 'error.main',
                                                }
                                              },
                                              '&:hover': {
                                                opacity: 0.9,
                                              }
                                            }}
                                          />
                                        </Tooltip>
                                      ))}
                                    </Box>
                                  );
                                })()}
                              </Stack>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleBoost(space.id)}
                                disabled={isLoading}
                                fullWidth
                              >
                                {isLoading ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  t('spaces.boost', 'Boost')
                                )}
                              </Button>
                            </Card>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                ) : (
                  <DataGrid
                    rows={spacesByLocation[location]}
                    columns={columns}
                    autoHeight
                    getRowId={(row) => row.id}
                    getRowHeight={() => 'auto'}
                    sx={{
                      width: '100%',
                      '& .MuiDataGrid-cell': {
                        display: 'flex',
                        alignItems: 'flex-start',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                      },
                      '& .MuiDataGrid-columnHeader': {
                        display: 'flex',
                        alignItems: 'center',
                      },
                      '& .MuiDataGrid-row': {
                        maxHeight: 'none !important',
                      },
                    }}
                  />
                )}
              </TabPanel>
            ))}
          </>
        )}
      </Card>

      {/* Boost Dialog */}
      <Dialog
        open={boostDialogOpen}
        onClose={handleCloseBoostDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: { xs: 0, sm: 3 },
            margin: { xs: 0, sm: 2 },
            maxWidth: { xs: '100%', sm: '500px' },
            boxShadow: mode === 'dark'
              ? '0px 8px 32px rgba(0, 0, 0, 0.6)'
              : '0px 4px 20px rgba(0, 0, 0, 0.12)',
            background: mode === 'dark' ? '#1F2129' : '#FFFFFF',
            border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6">
            {t('spaces.boostSpace', 'Boost Space')}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getCurrentLocale()}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
                  {t('spaces.boostPoints', 'Boost Points')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[0, 1].map((rowIndex) => (
                    <Box key={rowIndex} sx={{ display: 'flex', gap: 1 }}>
                      {boostPointOptions.slice(rowIndex * 5, (rowIndex + 1) * 5).map((points) => (
                        <Chip
                          key={points}
                          label={points}
                          onClick={() => setBoostPoints(points)}
                          color={boostPoints === points ? 'primary' : 'default'}
                          variant={boostPoints === points ? 'filled' : 'outlined'}
                          sx={{
                            cursor: 'pointer',
                            flex: 1,
                            fontWeight: boostPoints === points ? 600 : 400,
                          }}
                        />
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>

              <DatePicker
                label={t('spaces.expirationDate', 'Expiration Date')}
                value={expirationDate}
                onChange={(newValue) => setExpirationDate(newValue)}
                minDate={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
                        '&:hover': {
                          backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f0f2ff',
                        }
                      }
                    }
                  }
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={boostVisible}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBoostVisible(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('spaces.visibleOnSearch', 'Visible on search results')}
              />
            </Stack>
          </LocalizationProvider>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseBoostDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitBoost}
            disabled={!expirationDate || boostLoading !== null}
          >
            {boostLoading ? (
              <CircularProgress size={20} />
            ) : (
              t('spaces.applyBoost', 'Apply Boost')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={handleCloseReviewDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: { xs: 0, sm: 3 },
            margin: { xs: 0, sm: 2 },
            maxWidth: { xs: '100%', sm: '500px' },
            boxShadow: mode === 'dark'
              ? '0px 8px 32px rgba(0, 0, 0, 0.6)'
              : '0px 4px 20px rgba(0, 0, 0, 0.12)',
            background: mode === 'dark' ? '#1F2129' : '#FFFFFF',
            border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6">
            {t('spaces.createReview', 'Create Review')}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <FormControl fullWidth required>
              <InputLabel>{t('spaces.selectFakeUser', 'Select Fake User')}</InputLabel>
              <Select
                value={selectedFakeUserId}
                onChange={(e) => setSelectedFakeUserId(e.target.value)}
                label={t('spaces.selectFakeUser', 'Select Fake User')}
              >
                {fakeUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name} {user.bandName ? `(${user.bandName})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
                {t('spaces.rating', 'Rating')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Rating
                  value={reviewRating}
                  onChange={(event, newValue) => {
                    setReviewRating(newValue || 0);
                  }}
                  size="large"
                  sx={{
                    '& .MuiRating-iconFilled': {
                      color: '#4E67EB',
                    },
                    '& .MuiRating-iconHover': {
                      color: '#3d56d9',
                    },
                  }}
                />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {reviewRating}/5
                </Typography>
              </Box>
            </Box>

            <TextField
              fullWidth
              label={t('spaces.comment', 'Comment')}
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              multiline
              rows={4}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
                  '&:hover': {
                    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f0f2ff',
                  }
                }
              }}
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseReviewDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitReview}
            disabled={!selectedFakeUserId || !reviewComment.trim() || reviewRating < 1 || reviewRating > 5 || reviewLoading}
          >
            {reviewLoading ? (
              <CircularProgress size={20} />
            ) : (
              t('spaces.submitReview', 'Submit Review')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpacesPage;
